/**
 * VideosList - 2-column masonry grid for video search results with hover-to-play
 * Polished cards with rounded corners, smooth hover, always-visible attribution
 */

import { useState, useRef, useEffect, memo } from "react";

import { Download, Check, Copy, Link2, Search, Play, Loader2 } from "lucide-react";
import Masonry from "react-masonry-css";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { MediaLibraryResult } from "@/types/media-library-panel.types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatDuration,
  handleImageRetry,
  getProviderUrl,
  getProviderDisplayName,
  getAuthorUrl,
  getAttributionText,
} from "./media-utils";
import { useInfiniteScroll } from "./use-infinite-scroll";
import { Skeleton } from "@/components/ui/skeleton";

interface VideosListProps {
  results: MediaLibraryResult[];
  onDownload: (item: MediaLibraryResult) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  downloadingIds?: Set<string>;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const VideoCard = memo(
  ({
    result,
    onDownload,
    isSelected,
    onToggleSelect,
    isDownloading,
  }: {
    result: MediaLibraryResult;
    onDownload: () => void;
    isSelected?: boolean;
    onToggleSelect?: () => void;
    isDownloading?: boolean;
  }): React.ReactElement => {
    const [isHovered, setIsHovered] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const attribution = getAttributionText(result.provider, result.author, "video");

    const handleMouseEnter = (): void => {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(true);
        setShouldLoadVideo(true);
      }, 300);
    };

    const handleMouseLeave = (): void => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsHovered(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };

    useEffect(() => {
      if (shouldLoadVideo && videoRef.current && isHovered) {
        videoRef.current.load();
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    }, [shouldLoadVideo, isHovered]);

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const previewSrc = result.downloadUrl || result.previewUrl;

    return (
      <>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => setIsPreviewOpen(true)}
          className={cn(
            "relative overflow-hidden rounded-xl group cursor-pointer mb-2",
            "transition-all duration-200",
            isSelected
              ? "ring-2 ring-primary ring-offset-2 ring-offset-background shadow-md shadow-primary/10"
              : "hover:shadow-lg hover:shadow-black/10",
          )}
          style={
            result.width && result.height
              ? { aspectRatio: `${result.width}/${result.height}` }
              : undefined
          }
        >
          {/* Selection circle */}
          {onToggleSelect && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect();
              }}
              className={cn(
                "absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center z-40",
                "transition-all duration-200",
                isSelected
                  ? "bg-primary shadow-sm"
                  : "bg-black/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-black/50",
              )}
              aria-label={isSelected ? "Deselect" : "Select"}
            >
              {isSelected ? (
                <Check className="w-3 h-3 text-primary-foreground" />
              ) : (
                <div className="w-2 h-2 rounded-full border-[1.5px] border-white" />
              )}
            </button>
          )}

          {/* Thumbnail */}
          <img
            src={result.previewUrl}
            alt={result.title}
            className={cn(
              "w-full h-full object-cover transition-all duration-500 ease-out",
              isHovered && isVideoReady ? "opacity-0" : "opacity-100",
              !isHovered && "group-hover:scale-[1.03]",
            )}
            loading="lazy"
            decoding="async"
            onError={(e) => handleImageRetry(e, result.previewUrl)}
          />

          {/* Video element */}
          {shouldLoadVideo && (
            <video
              ref={videoRef}
              src={result.downloadUrl}
              onLoadedData={() => setIsVideoReady(true)}
              poster={result.previewUrl}
              className={cn(
                "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
                isHovered && isVideoReady ? "opacity-100" : "opacity-0",
              )}
              muted
              loop
              playsInline
              preload="none"
            />
          )}

          {/* Play icon - centered, subtle */}
          {!isHovered && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
              <div className="w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-3.5 h-3.5 text-white ml-0.5" />
              </div>
            </div>
          )}

          {/* Download button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isDownloading) onDownload();
            }}
            disabled={isDownloading}
            className={cn(
              "absolute top-2 right-2 w-6 h-6 rounded-full",
              "bg-black/30 backdrop-blur-sm flex items-center justify-center",
              "transition-all duration-200 z-30",
              isDownloading
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 hover:bg-black/50",
            )}
            aria-label="Download"
          >
            {isDownloading ? (
              <Loader2 className="w-3 h-3 text-white animate-spin" />
            ) : (
              <Download className="w-3 h-3 text-white" />
            )}
          </button>

          {/* Always-visible 2-line attribution: provider + duration top, author bottom */}
          <div className="absolute bottom-0 inset-x-0 pt-8 pb-1.5 px-2 bg-gradient-to-t from-black/55 via-black/25 to-transparent z-10">
            <div className="flex items-center justify-between gap-1">
              <a
                href={getProviderUrl(result.provider)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[9px] text-white/60 font-medium leading-tight drop-shadow-sm hover:text-white/80 transition-colors truncate"
              >
                {getProviderDisplayName(result.provider)}
              </a>
              {result.duration != null && (
                <span className="text-[10px] text-white/90 font-semibold tabular-nums shrink-0 drop-shadow-sm">
                  {formatDuration(result.duration)}
                </span>
              )}
            </div>
            {result.author && (
              <a
                href={result.authorUrl ? getAuthorUrl(result.provider, result.authorUrl) : getProviderUrl(result.provider)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="block text-[10px] text-white/90 font-medium leading-tight drop-shadow-sm hover:text-white transition-colors truncate"
              >
                {result.author}
              </a>
            )}
          </div>
        </div>

        {/* Preview dialog - responsive: full-screen on mobile, centered on desktop */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-[92vw] sm:max-w-3xl max-h-[85dvh] p-0 overflow-hidden bg-black/95 border-border gap-0 rounded-lg">
            <DialogTitle className="sr-only">{result.title}</DialogTitle>
            <div className="relative flex items-center justify-center">
              <video
                src={result.downloadUrl}
                poster={result.previewUrl}
                controls
                autoPlay
                className="w-full object-contain max-h-[60dvh]"
                playsInline
              />
            </div>
            <div className="px-4 py-3 bg-background space-y-2 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{result.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {result.author && result.authorUrl ? (
                      <a
                        href={getAuthorUrl(result.provider, result.authorUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground underline"
                      >
                        {attribution}
                      </a>
                    ) : (
                      attribution
                    )}
                    {result.duration != null && ` · ${formatDuration(result.duration)}`}
                    {result.width && result.height && ` · ${result.width}×${result.height}`}
                  </p>
                </div>
                <button
                  onClick={() => { if (!isDownloading) onDownload(); }}
                  disabled={isDownloading}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors shrink-0",
                    "px-3.5 py-1.5",
                    isDownloading
                      ? "bg-primary/70 text-primary-foreground cursor-not-allowed"
                      : "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {isDownloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  {isDownloading ? "Downloading..." : "Download"}
                </button>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border/50 overflow-hidden">
                <Link2 className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
                <a
                  href={previewSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 text-xs text-muted-foreground/70 hover:text-foreground transition-colors truncate font-mono"
                  title={previewSrc}
                >
                  {previewSrc}
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0 hover:bg-muted-foreground/10"
                  onClick={() => {
                    navigator.clipboard.writeText(previewSrc);
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 1200);
                  }}
                >
                  {isCopied ? (
                    <Check className="w-3 h-3 text-primary" />
                  ) : (
                    <Copy className="w-3 h-3 text-muted-foreground/50" />
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);
VideoCard.displayName = "VideoCard";

function VideoSkeleton(): React.ReactElement {
  return (
    <Skeleton
      className="relative overflow-hidden rounded-xl mb-2"
      style={{ aspectRatio: Math.random() > 0.6 ? "16/9" : "9/16" }}
    />
  );
}

function VideosList({
  results,
  onDownload,
  selectedIds,
  onToggleSelect,
  downloadingIds,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: VideosListProps): React.ReactElement | null {
  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadMore || (() => {}),
    hasMore,
    isLoading: isLoadingMore,
    threshold: 500,
    debounceMs: 300,
  });

  const breakpointColumns = {
    default: 3,
    1280: 3,
    1024: 3,
    768: 3,
    640: 3,
  };

  if (isLoading) {
    return (
      <Masonry
        breakpointCols={breakpointColumns}
        className="flex -ml-2 w-auto"
        columnClassName="pl-2 bg-clip-padding"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <VideoSkeleton key={i} />
        ))}
      </Masonry>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
          <Search className="w-5 h-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">No results found</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Try different keywords or change filters
        </p>
      </div>
    );
  }

  return (
    <>
      <Masonry
        breakpointCols={breakpointColumns}
        className="flex -ml-2 w-auto"
        columnClassName="pl-2 bg-clip-padding"
      >
        {results.map((result) => {
          const cardId = `${result.provider}-${result.id}`;
          return (
            <VideoCard
              key={cardId}
              result={result}
              onDownload={() => onDownload(result)}
              isSelected={selectedIds?.has(cardId)}
              onToggleSelect={
                onToggleSelect ? () => onToggleSelect(cardId) : undefined
              }
              isDownloading={downloadingIds?.has(cardId)}
            />
          );
        })}
        {isLoadingMore &&
          Array.from({ length: 4 }).map((_, i) => (
            <VideoSkeleton key={`loading-${i}`} />
          ))}
      </Masonry>

      <div ref={sentinelRef} className="h-px" />
    </>
  );
}

export { VideosList, VideoCard };
